import api from './api';
import { Report } from '../types';

export interface ReportPdfLink {
  url: string;
  expiresAt: number;
}

// Browsers cannot send the Bearer header from an iframe or a new tab, so the viewer uses a
// short-lived, report-scoped link minted by an authenticated POST (never the old public uploads folder).
// Expiry uses the local clock plus the server TTL, so client/server clock skew does not matter.
export const fetchReportPdfLink = async (reportId: string): Promise<ReportPdfLink> => {
  const res = await api.post(`/reports/${reportId}/pdf-link`);
  const d = res.data.data;
  return {
    url: `${api.defaults.baseURL}${d.path}`,
    expiresAt: Date.now() + d.expiresInSeconds * 1000,
  };
};

export const pdfDownloadName = (report: Pick<Report, '_id' | 'orderId' | 'originalFileName'>): string => {
  const raw = (report.originalFileName || '').trim() || `report-${report.orderId || report._id}.pdf`;
  return /\.pdf$/i.test(raw) ? raw : `${raw}.pdf`;
};

// Download through a Bearer XHR: no token ever goes into a URL, and errors come back as data
// (the page never navigates to a JSON error). Safari cancels a download whose object URL is
// revoked immediately, hence the 60 s delay before revoking.
export const downloadReportPdf = async (report: Pick<Report, '_id' | 'orderId' | 'originalFileName'>): Promise<void> => {
  const res = await api.get(`/reports/${report._id}/pdf`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = pdfDownloadName(report);
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// Error bodies of a responseType 'blob' request arrive as a Blob, so parse the JSON out of it.
export const pdfErrorMessage = async (err: any): Promise<string> => {
  const data = err?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const j = JSON.parse(await data.text());
      if (j?.message) return j.message;
    } catch {
      /* not JSON */
    }
    return 'Could not download the PDF';
  }
  return data?.message || 'Could not open the PDF';
};
